/**
 * Fields the doctor changes on the prescription object itself, rather than in the writer's form,
 * and that reach the server only with the next Save. Today that is the bedside blood sugar tick,
 * which the Lab Tests card owns (BedsideSugarCheck) and handleSave sends.
 *
 * Everything below is driven by this list, so a field added here is kept across refreshes,
 * counted as unsaved work, and carried in the recovery draft without any other change.
 */
export const LOCAL_ONLY_RX_FIELDS = ['bedsideBloodSugar'];

const isObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/**
 * A fresh copy of the prescription from the server, keeping the doctor's unsaved local fields.
 *
 * Adding or removing a medicine or lab test (and applying a template, or copying a previous
 * prescription) answers with the whole prescription as the server holds it. Taking that as it is
 * would quietly undo a tick the doctor has not saved yet. Use it as
 * `setPrescription(prev => keepLocalRxEdits(prev, fresh))` wherever the writer refreshes the
 * prescription it is still editing. Do not use it where the server's copy must win: after a save
 * or completion returns (the save has just sent these fields), or when a prescription is opened,
 * switched or reloaded.
 *
 * Every caller refreshes the prescription on screen, so an answer that is not for it is stale: it
 * landed after the doctor closed this prescription (prev is null) or opened another one (the ids
 * differ). The screen is left as it is then, rather than showing the old prescription again. An
 * empty answer leaves the screen as it is too.
 */
export const keepLocalRxEdits = (prev, fresh) => {
  if (!isObject(fresh)) return prev;
  if (!isObject(prev)) return prev;
  if (prev.id != null && fresh.id != null && String(prev.id) !== String(fresh.id)) return prev;
  const kept = {};
  LOCAL_ONLY_RX_FIELDS.forEach((field) => {
    if (prev[field] !== undefined) kept[field] = prev[field];
  });
  return { ...fresh, ...kept };
};

/** The local-only fields of a prescription, as a small object (missing ones as null). */
export const localRxFields = (rx) => {
  const out = {};
  LOCAL_ONLY_RX_FIELDS.forEach((field) => {
    out[field] = isObject(rx) && rx[field] !== undefined ? rx[field] : null;
  });
  return out;
};

// Unset, null, false and '' all mean "not set": a prescription the server has never saved the tick
// on sends null, and ticking then unticking the box leaves false. Neither is a change.
const normalise = (v) => (v === undefined || v === null || v === false || v === '' ? null : v);

/**
 * Whether the prescription's local-only fields differ from the saved ones (a localRxFields
 * snapshot taken when the prescription was opened or last saved): unsaved work the form's own
 * dirty check cannot see, since these fields are not in formData.
 */
export const localRxEditsDiffer = (saved, rx) =>
  LOCAL_ONLY_RX_FIELDS.some(
    (field) => JSON.stringify(normalise(saved?.[field])) !== JSON.stringify(normalise(rx?.[field]))
  );

/**
 * The prescription with local-only fields put back from a recovery draft. Only the listed fields
 * are taken from the draft, whatever else it holds.
 */
export const restoreLocalRxEdits = (prev, savedLocal) => {
  if (!isObject(prev) || !isObject(savedLocal)) return prev;
  const restored = {};
  LOCAL_ONLY_RX_FIELDS.forEach((field) => {
    if (savedLocal[field] !== undefined) restored[field] = savedLocal[field];
  });
  return Object.keys(restored).length > 0 ? { ...prev, ...restored } : prev;
};
